/**
 * Close: do the month's bank alerts agree with the statements?
 *
 * match_provisional (run when a statement is processed) marks each alert row for that account
 * matched or unmatched. Rows still 'provisional' belong to an account with no processed statement
 * yet, or arrived after it was processed. Nothing here blocks publishing: statements are the record,
 * alerts are the cross-check.
 */
import type { Account, ProvisionalTransaction, Upload } from '../data/types';

export interface AlertCheck {
  accountId: string;
  label: string;
  total: number;
  matched: number;
  /** Alerts the processed statement does not contain, oldest first. */
  unmatched: ProvisionalTransaction[];
  /** Alerts not yet compared with any statement. */
  unchecked: number;
  hasStatement: boolean;
}

export function alertCheck(rows: ProvisionalTransaction[], uploads: Upload[], accounts: Account[]): AlertCheck[] {
  const processed = new Set(uploads.filter((u) => u.processing_status === 'processed' && u.account_id).map((u) => u.account_id));
  const name = new Map(accounts.map((a) => [a.id, a.name]));
  const by = new Map<string, ProvisionalTransaction[]>();
  for (const r of rows) by.set(r.account_id, [...(by.get(r.account_id) ?? []), r]);
  return [...by.entries()]
    .map(([id, rs]) => ({
      accountId: id,
      label: name.get(id) ?? rs[0]!.account_label ?? rs[0]!.bank,
      total: rs.length,
      matched: rs.filter((r) => r.status === 'matched').length,
      unmatched: rs.filter((r) => r.status === 'unmatched').sort((a, b) => a.occurred_at.localeCompare(b.occurred_at)),
      unchecked: rs.filter((r) => r.status === 'provisional').length,
      hasStatement: processed.has(id),
    }))
    .sort((a, b) => b.unmatched.length - a.unmatched.length || a.label.localeCompare(b.label));
}

/** Processed uploads whose account still has unchecked alerts: run matching for these again. */
export function needsRematch(rows: ProvisionalTransaction[], uploads: Upload[]): string[] {
  const waiting = new Set(rows.filter((r) => r.status === 'provisional').map((r) => r.account_id));
  return uploads.filter((u) => u.processing_status === 'processed' && u.account_id && waiting.has(u.account_id)).map((u) => u.id);
}

/** One line per account, in plain words. */
export function checkLine(c: AlertCheck): string {
  if (!c.hasStatement && !c.unmatched.length) return `${c.total} alert${c.total === 1 ? '' : 's'}, no statement processed yet`;
  if (!c.unmatched.length && !c.unchecked) return c.total === 1 ? 'the 1 alert is on the statement' : `all ${c.total} alerts are on the statement`;
  const parts = [`${c.matched} of ${c.total} alerts are on the statement`];
  if (c.unmatched.length) parts.push(`${c.unmatched.length} not found`);
  if (c.unchecked) parts.push(`${c.unchecked} not checked yet`);
  return parts.join(', ');
}
